import { isEmptyObject } from "./is-true";

export type ResolverEntry = {
  fn: (value: unknown, context: unknown) => unknown | undefined;
  outputKey: string;
};

export type ResolverEntryMap = Record<string, ResolverEntry>;

/**
 * Resolves values from rawValues based on the provided RESOLVERS map.
 * Only resolved values that are not 'undefined' are included in the output.
 *
 * @param rawValues The input object containing raw field values.
 * @param RESOLVERS A map from input key to a ResolverEntry defining the function and output key.
 * @returns A Record<string, unknown> containing the resolved values.
 */
export function resolveValues(
  rawValues: Record<string, unknown>,
  resolverEntryMap: ResolverEntryMap,
  context?: Record<string, unknown>,
): Record<string, unknown> | null {
  const resolved: Record<string, unknown> = {};

  Object.entries(resolverEntryMap).forEach(([inputKey, { fn, outputKey }]) => {
    // 1. Check if the rawValues object has the property for this resolver
    if (Object.hasOwn(rawValues, inputKey)) {
      const value = rawValues[inputKey];

      // 2. Resolve the value
      const resolvedValue = fn(value, context);

      // 3. Only include if the resolved value is NOT undefined
      if (resolvedValue !== undefined) {
        resolved[outputKey] = resolvedValue;
      }
    }
  });

  if (isEmptyObject(resolved)) {
    return null;
  }

  return resolved;
}
